import React, { useCallback, useEffect, useState } from 'react'
import {
  X,
  Printer,
  Ban,
  RefreshCw,
  AlertTriangle,
  FileText,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { shouldCloseDialog, useEscapeClose } from '@/hooks/useEscapeClose'
import { formatFreeStockDetails } from '@/services/warehouse/freeStockApi'
import {
  getIssueSlip,
  voidIssueSlip,
  type IssueSlipDetail,
} from '@/services/warehouse/issueSlipsApi'

interface IssueSlipDetailModalProps {
  slipId: string | null
  onClose: () => void
  onPrint: (slip: IssueSlipDetail) => void
  onVoidSuccess: () => void
  initialDetail?: IssueSlipDetail | null
}

function formatDateTimeVi(iso: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function IssueSlipDetailModal({
  slipId,
  onClose,
  onPrint,
  onVoidSuccess,
  initialDetail,
}: IssueSlipDetailModalProps): React.ReactElement | null {
  const [slip, setSlip] = useState<IssueSlipDetail | null>(initialDetail ?? null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Void confirmation dialog state
  const [isVoidDialogOpen, setIsVoidDialogOpen] = useState(false)
  const [voidReason, setVoidReason] = useState('')
  const [voiding, setVoiding] = useState(false)
  const [voidError, setVoidError] = useState<string | null>(null)

  const closeVoidDialog = useCallback(() => {
    if (!shouldCloseDialog(Boolean(voidReason.trim()))) return
    setIsVoidDialogOpen(false)
  }, [voidReason])
  const closeDetail = useCallback(() => {
    if (isVoidDialogOpen) {
      closeVoidDialog()
      return
    }
    onClose()
  }, [closeVoidDialog, isVoidDialogOpen, onClose])
  useEscapeClose({ enabled: Boolean(slipId), hasUnsavedChanges: isVoidDialogOpen && Boolean(voidReason.trim()), onClose: closeDetail })

  useEffect(() => {
    if (initialDetail !== undefined) {
      setSlip(initialDetail)
      return
    }
    if (!slipId) {
      setSlip(null)
      setError(null)
      return
    }
    let isMounted = true
    setLoading(true)
    setError(null)

    getIssueSlip(slipId)
      .then((data) => {
        if (isMounted) setSlip(data)
      })
      .catch((err: any) => {
        if (isMounted) setError(err?.message || 'Không thể tải chi tiết phiếu xuất')
      })
      .finally(() => {
        if (isMounted) setLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [slipId, initialDetail])

  async function handleConfirmVoid() {
    if (!slip) return
    if (!voidReason.trim()) {
      setVoidError('Vui lòng nhập lý do hủy phiếu xuất kho (bắt buộc)')
      return
    }

    setVoiding(true)
    setVoidError(null)
    try {
      const updated = await voidIssueSlip(slip.id, voidReason.trim())
      setSlip(updated)
      setIsVoidDialogOpen(false)
      setVoidReason('')
      onVoidSuccess()
    } catch (err: any) {
      setVoidError(err?.message || 'Lỗi khi hủy phiếu xuất kho')
    } finally {
      setVoiding(false)
    }
  }

  if (!slipId) return null

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 backdrop-blur-xs font-sans"
        role="dialog"
        aria-modal="true"
        aria-labelledby="issue-slip-detail-title"
      >
        <div className="flex max-h-[90vh] w-full max-w-5xl flex-col rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-white shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-[var(--color-border)] px-5 py-3.5">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-slate-700" />
              <div>
                <h2 id="issue-slip-detail-title" className="text-base font-bold text-slate-900">
                  Chi tiết phiếu xuất kho {slip?.code ? `· ${slip.code}` : ''}
                </h2>
                <p className="text-xs text-slate-500">
                  Thông tin các dòng xuất và điều chuyển kho
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={closeDetail}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              aria-label="Đóng"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-5">
            {loading && !slip ? (
              <div className="flex items-center justify-center py-12 text-slate-400 text-sm">
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Đang tải thông tin phiếu xuất kho...
              </div>
            ) : error ? (
              <div className="rounded-[var(--radius-md)] border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
                {error}
              </div>
            ) : slip ? (
              <div className="space-y-4">
                {/* Status banner */}
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-[var(--radius-md)] border border-slate-200 bg-slate-50 p-3">
                  <div className="flex items-center gap-3">
                    <span
                      className={`inline-block rounded-md border px-2.5 py-1 text-xs font-bold uppercase tracking-wider ${
                        slip.status === 'ACTIVE'
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                          : 'border-rose-200 bg-rose-50 text-rose-800'
                      }`}
                    >
                      {slip.status === 'ACTIVE' ? 'Hoạt động' : 'Đã hủy'}
                    </span>
                    <span className="font-mono text-xs font-semibold text-slate-700">
                      Mã: {slip.code}
                    </span>
                  </div>

                  <div className="text-xs text-slate-500">
                    Ngày lập: <b className="text-slate-800">{formatDateTimeVi(slip.created_at)}</b> · Người lập:{' '}
                    <b className="text-slate-800">{slip.created_by_name || slip.created_by}</b>
                  </div>
                </div>

                {/* Purpose & Note */}
                <div className="grid grid-cols-1 gap-3 rounded-[var(--radius-md)] border border-slate-200 p-3 text-xs sm:grid-cols-2">
                  <div>
                    <span className="font-semibold text-slate-500">Mục đích xuất kho:</span>
                    <div className="mt-0.5 font-medium text-slate-900 break-words">{slip.purpose}</div>
                  </div>
                  <div>
                    <span className="font-semibold text-slate-500">Ghi chú phiếu:</span>
                    <div className="mt-0.5 text-slate-700 break-words">{slip.note || '—'}</div>
                  </div>

                  {slip.status === 'VOID' ? (
                    <div className="sm:col-span-2 rounded border border-rose-200 bg-rose-50 p-2 text-rose-800">
                      <span className="font-bold">Lý do hủy ({formatDateTimeVi(slip.voided_at || '')}): </span>
                      <span>{slip.void_reason || '—'}</span>
                    </div>
                  ) : null}
                </div>

                {/* Table of lines */}
                <div>
                  <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-700">
                    Danh sách mặt hàng đã xuất ({slip.lines.length} dòng)
                  </h3>

                  <div className="overflow-x-auto rounded-[var(--radius-md)] border border-[var(--color-border)]">
                    <table className="w-full border-collapse text-left text-xs">
                      <thead className="border-b border-[var(--color-border)] bg-slate-50 font-semibold text-slate-600 uppercase">
                        <tr>
                          <th className="w-12 px-3 py-2 text-center whitespace-nowrap">STT</th>
                          <th className="min-w-[180px] px-3 py-2 whitespace-nowrap">Tên hàng / Vật tư</th>
                          <th className="min-w-[180px] px-3 py-2 whitespace-nowrap">Chi tiết</th>
                          <th className="w-20 min-w-[65px] px-3 py-2 text-center whitespace-nowrap">ĐVT</th>
                          <th className="w-28 min-w-[110px] px-3 py-2 text-right whitespace-nowrap">Số lượng xuất</th>
                          <th className="min-w-[160px] px-3 py-2">Ghi chú</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {slip.lines.map((line, idx) => {
                          const detailsStr = formatFreeStockDetails(line.details_snapshot)
                          return (
                            <tr key={line.id} className="hover:bg-slate-50/80">
                              <td className="px-3 py-2 text-center text-slate-400 tabular-nums whitespace-nowrap">
                                {idx + 1}
                              </td>
                              <td className="px-3 py-2 font-semibold text-slate-900 break-words">
                                {line.label_snapshot}
                              </td>
                              <td className="px-3 py-2 text-slate-600 break-words">
                                {detailsStr}
                              </td>
                              <td className="px-3 py-2 text-center text-slate-700 whitespace-nowrap">
                                {line.uom}
                              </td>
                              <td className="px-3 py-2 text-right font-bold text-slate-900 tabular-nums whitespace-nowrap">
                                {line.qty.toLocaleString('vi-VN')}
                              </td>
                              <td className="px-3 py-2 text-slate-600 break-words">
                                {line.note || '—'}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {/* Footer actions */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[var(--color-border)] px-5 py-3">
            <div>
              {slip && slip.status === 'ACTIVE' ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsVoidDialogOpen(true)}
                  className="border-rose-200 text-rose-700 hover:bg-rose-50 hover:border-rose-300"
                >
                  <Ban className="mr-1.5 h-3.5 w-3.5" />
                  Hủy phiếu xuất kho
                </Button>
              ) : null}
            </div>

            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" onClick={closeDetail}>
                Đóng
              </Button>
              {slip ? (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => onPrint(slip)}
                >
                  <Printer className="mr-1.5 h-3.5 w-3.5" />
                  In phiếu xuất (A5)
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Void Prompt Dialog */}
      {isVoidDialogOpen ? (
        <div
          className="fixed inset-0 z-60 flex items-center justify-center bg-black/60 p-4 font-sans"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-md rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-white p-5 shadow-2xl">
            <div className="flex items-center gap-2 text-rose-600">
              <AlertTriangle className="h-5 w-5" />
              <h3 className="text-sm font-bold text-slate-900">
                Xác nhận hủy phiếu xuất kho {slip?.code}
              </h3>
            </div>
            <p className="mt-2 text-xs text-slate-600">
              Thao tác hủy phiếu sẽ hoàn lại toàn bộ số lượng vào tồn kho vật tư và kho hàng tự do tương ứng. Thao tác này không thể hoàn tác.
            </p>

            {voidError ? (
              <div className="mt-3 rounded border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">
                {voidError}
              </div>
            ) : null}

            <div className="mt-3">
              <label className="mb-1 block text-xs font-bold text-slate-700">
                Lý do hủy <span className="text-rose-600">*</span>
              </label>
              <textarea
                rows={3}
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder="Nhập lý do hủy phiếu xuất kho (bắt buộc)..."
                className="w-full rounded border border-slate-300 p-2 text-xs outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
              />
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={closeVoidDialog}
                disabled={voiding}
              >
                Hủy bỏ
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleConfirmVoid}
                disabled={voiding || !voidReason.trim()}
                className="bg-rose-600 hover:bg-rose-700"
              >
                {voiding ? (
                  <>
                    <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    Đang hủy...
                  </>
                ) : (
                  'Xác nhận hủy phiếu'
                )}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
